import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: 'at_warehouse', label: 'At Warehouse' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'arrived_ghana', label: 'Arrived in Ghana' },
  { value: 'ready_for_pickup', label: 'Ready for Pickup' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
];

function toDateInput(value) {
  return value ? String(value).split('T')[0] : '';
}

function buildDraft(item) {
  return {
    status: item.status || 'at_warehouse',
    container_loaded_date: toDateInput(item.container_loaded_date),
    eta_ghana: toDateInput(item.eta_ghana),
  };
}

function formatStatus(status) {
  const match = STATUS_OPTIONS.find((option) => option.value === status);
  return match ? match.label : String(status || '').replace(/_/g, ' ');
}

export default function AdminBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [items, setItems] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [itemDrafts, setItemDrafts] = useState({});
  const [batchDrafts, setBatchDrafts] = useState({});
  const [savingItems, setSavingItems] = useState({});
  const [savingBatches, setSavingBatches] = useState({});

  useEffect(() => {
    axios.get('/api/admin/shipments')
      .then((response) => setBatches(response.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmt = (value) => parseFloat(value || 0).toFixed(2);

  const loadItems = async (uploadId) => {
    if (expanded === uploadId) {
      setExpanded(null);
      return;
    }

    setExpanded(uploadId);
    if (items[uploadId]) return;

    try {
      const response = await axios.get(`/api/admin/shipments/${uploadId}/items`);
      setItems((previous) => ({ ...previous, [uploadId]: response.data }));
      setSelectedItems((previous) => ({ ...previous, [uploadId]: [] }));
      setBatchDrafts((previous) => ({
        ...previous,
        [uploadId]: previous[uploadId] || { status: 'at_warehouse', container_loaded_date: '', eta_ghana: '' },
      }));
    } catch {}
  };

  const patchItemInState = (uploadId, itemIds, updates) => {
    const itemIdSet = new Set(itemIds);
    setItems((previous) => ({
      ...previous,
      [uploadId]: (previous[uploadId] || []).map((item) => (
        itemIdSet.has(item.id)
          ? {
              ...item,
              status: updates.status,
              container_loaded_date: updates.container_loaded_date === ''
                ? item.container_loaded_date
                : updates.container_loaded_date || null,
              eta_ghana: updates.eta_ghana === ''
                ? item.eta_ghana
                : updates.eta_ghana || null,
            }
          : item
      )),
    }));
  };

  const toggleItemSelection = (uploadId, itemId) => {
    setSelectedItems((previous) => {
      const current = previous[uploadId] || [];
      return {
        ...previous,
        [uploadId]: current.includes(itemId)
          ? current.filter((value) => value !== itemId)
          : [...current, itemId],
      };
    });
  };

  const toggleSelectAll = (uploadId) => {
    const batchItems = items[uploadId] || [];
    const current = selectedItems[uploadId] || [];
    const next = current.length === batchItems.length ? [] : batchItems.map((item) => item.id);
    setSelectedItems((previous) => ({ ...previous, [uploadId]: next }));
  };

  const updateItemDraft = (item, patch) => {
    setItemDrafts((previous) => ({
      ...previous,
      [item.id]: {
        ...(previous[item.id] || buildDraft(item)),
        ...patch,
      },
    }));
  };

  const updateBatchDraft = (uploadId, patch) => {
    setBatchDrafts((previous) => ({
      ...previous,
      [uploadId]: {
        ...(previous[uploadId] || { status: 'at_warehouse', container_loaded_date: '', eta_ghana: '' }),
        ...patch,
      },
    }));
  };

  const saveSingleItem = async (uploadId, item) => {
    const draft = itemDrafts[item.id] || buildDraft(item);
    setSavingItems((previous) => ({ ...previous, [item.id]: true }));

    try {
      const response = await axios.put(`/api/admin/shipments/items/${item.id}/status`, draft);
      patchItemInState(uploadId, [item.id], draft);
      toast.success(response.data.updated ? 'Shipment updated' : 'No shipment changes detected');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update shipment');
    } finally {
      setSavingItems((previous) => ({ ...previous, [item.id]: false }));
    }
  };

  const saveBatchItems = async (uploadId) => {
    const ids = selectedItems[uploadId] || [];
    if (!ids.length) {
      toast.error('Select at least one item');
      return;
    }

    const draft = batchDrafts[uploadId] || { status: 'at_warehouse', container_loaded_date: '', eta_ghana: '' };
    setSavingBatches((previous) => ({ ...previous, [uploadId]: true }));

    try {
      const response = await axios.put('/api/admin/shipments/items/status/bulk', {
        item_ids: ids,
        ...draft,
      });

      if (response.data.updatedCount > 0) {
        patchItemInState(uploadId, ids, draft);
      }

      setSelectedItems((previous) => ({ ...previous, [uploadId]: [] }));
      toast.success(
        response.data.updatedCount > 0
          ? `Updated ${response.data.updatedCount} shipment(s)`
          : 'No shipment changes detected'
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to bulk update shipments');
    } finally {
      setSavingBatches((previous) => ({ ...previous, [uploadId]: false }));
    }
  };

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn">←</Link>
        <h1>Upload Batches</h1>
        <Link to="/admin/upload" className="btn btn-primary btn-sm">+ New</Link>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="loading-center"><div className="spinner spinner-dark" /></div>
        ) : batches.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📋</div>
            <p style={{ fontWeight: 600 }}>No batches yet</p>
            <p>Upload your first warehouse sheet to get started.</p>
          </div>
        ) : batches.map((batch) => {
          const isSea = batch.transport_mode === 'sea';
          const isOpen = expanded === batch.id;
          const batchItems = items[batch.id] || [];
          const selected = selectedItems[batch.id] || [];
          const batchDraft = batchDrafts[batch.id] || { status: 'at_warehouse', container_loaded_date: '', eta_ghana: '' };
          const paidCount = batchItems.filter((item) => item.payment_status === 'paid').length;
          const unpaidCount = batchItems.filter((item) => item.payment_status === 'unpaid').length;
          const pendingCount = batchItems.filter((item) => item.payment_status === 'pending').length;

          return (
            <div key={batch.id} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 20,
                      background: isSea ? '#e0f2fe' : '#f0fdf4',
                      color: isSea ? '#0369a1' : '#15803d'
                    }}>
                      {isSea ? 'SEA' : 'AIR'}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed' }}>
                      {batch.route === 'CN_GH' ? 'CN→GH' : 'TR→GH'}
                    </span>
                  </div>
                  <p style={{ fontWeight: 700, fontSize: 14 }}>{batch.filename}</p>
                  <p style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    Batch #{batch.id} · Uploaded by {batch.uploader_name}
                  </p>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                  <p style={{ fontSize: 11, color: '#9ca3af' }}>
                    {new Date(batch.upload_timestamp).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  <p style={{ fontSize: 11, color: '#9ca3af' }}>
                    {new Date(batch.upload_timestamp).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#6b7280' }}>
                <span style={{ marginRight: 12 }}>
                  {isSea ? `CBM Rate: $${fmt(batch.cbm_rate_usd)} USD` : `KG Rate: $${fmt(batch.kg_rate_usd)} USD`}
                </span>
                <span>Rate used: 1 USD = {parseFloat(batch.exchange_rate || 1).toFixed(2)} GHS</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                  <p style={{ fontSize: 18, fontWeight: 800, color: '#1A6B8A' }}>{batch.item_count || batch.total_items || 0}</p>
                  <p style={{ fontSize: 10, color: '#6b7280' }}>Total Items</p>
                </div>
                <div style={{ background: '#dcfce7', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                  <p style={{ fontSize: 14, fontWeight: 800, color: '#15803d' }}>GHS {fmt(batch.total_ghs)}</p>
                  <p style={{ fontSize: 10, color: '#166534' }}>Expected Total</p>
                </div>
                <div style={{ background: '#fee2e2', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                  <p style={{ fontSize: 14, fontWeight: 800, color: '#dc2626' }}>GHS {fmt(batch.outstanding_ghs)}</p>
                  <p style={{ fontSize: 10, color: '#991b1b' }}>Outstanding</p>
                </div>
              </div>

              {parseFloat(batch.collected_ghs) > 0 && (
                <div style={{ background: '#dcfce7', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>Collected</span>
                  <span style={{ fontSize: 13, color: '#15803d', fontWeight: 800 }}>GHS {fmt(batch.collected_ghs)}</span>
                </div>
              )}

              <button
                onClick={() => loadItems(batch.id)}
                className="btn btn-outline btn-sm btn-full"
                style={{ marginBottom: isOpen ? 12 : 0 }}
              >
                {isOpen ? 'Hide Items' : `View ${batch.total_items} Items`}
              </button>

              {isOpen && (
                <div style={{ marginTop: 4 }}>
                  {batchItems.length > 0 && (
                    <>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                        {paidCount > 0 && <span className="badge badge-paid">{paidCount} paid</span>}
                        {pendingCount > 0 && <span className="badge badge-pending">{pendingCount} pending</span>}
                        {unpaidCount > 0 && <span className="badge badge-unpaid">{unpaidCount} unpaid</span>}
                      </div>

                      <div style={{ background: '#f8fafc', borderRadius: 10, padding: 12, marginBottom: 12, border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                          <div>
                            <p style={{ fontWeight: 700, fontSize: 13 }}>Bulk shipment status update</p>
                            <p style={{ fontSize: 11, color: '#6b7280' }}>
                              Apply one shipment status to selected items in this batch.
                            </p>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selected.length > 0 && selected.length === batchItems.length}
                              onChange={() => toggleSelectAll(batch.id)}
                            />
                            Select all
                          </label>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                          <div className="input-group" style={{ marginBottom: 0 }}>
                            <label>Status</label>
                            <select
                              className="input-field"
                              value={batchDraft.status}
                              onChange={(event) => updateBatchDraft(batch.id, { status: event.target.value })}
                            >
                              {STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="input-group" style={{ marginBottom: 0 }}>
                            <label>Container Loaded</label>
                            <input
                              className="input-field"
                              type="date"
                              value={batchDraft.container_loaded_date}
                              onChange={(event) => updateBatchDraft(batch.id, { container_loaded_date: event.target.value })}
                            />
                          </div>
                          <div className="input-group" style={{ marginBottom: 0 }}>
                            <label>ETA Ghana</label>
                            <input
                              className="input-field"
                              type="date"
                              value={batchDraft.eta_ghana}
                              onChange={(event) => updateBatchDraft(batch.id, { eta_ghana: event.target.value })}
                            />
                          </div>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => saveBatchItems(batch.id)}
                            disabled={savingBatches[batch.id] || selected.length === 0}
                          >
                            {savingBatches[batch.id] ? <span className="spinner" /> : `Apply (${selected.length})`}
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {batchItems.length === 0 ? (
                    <div className="loading-center"><div className="spinner spinner-dark" /></div>
                  ) : batchItems.map((item) => {
                    const draft = itemDrafts[item.id] || buildDraft(item);
                    const isSaving = !!savingItems[item.id];
                    const checked = selected.includes(item.id);

                    return (
                      <div key={item.id} style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, paddingBottom: 12 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleItemSelection(batch.id, item.id)}
                            style={{ marginTop: 4 }}
                          />

                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                              <div style={{ flex: 1 }}>
                                <p style={{ fontWeight: 600, fontSize: 13 }}>{item.goods_name || '—'}</p>
                                <p style={{ fontSize: 11, color: '#6b7280' }}>
                                  {item.full_name || 'Unmatched'} · {item.shipping_mark}
                                </p>
                                <p style={{ fontSize: 11, color: '#9ca3af' }}>#{item.tracking_number || 'N/A'}</p>
                                <p style={{ fontSize: 11, color: isSea ? '#0369a1' : '#15803d', marginTop: 2 }}>
                                  {isSea
                                    ? `${parseFloat(item.volume_cbm || 0).toFixed(4)} CBM × $${fmt(batch.cbm_rate_usd)}/CBM`
                                    : `${parseFloat(item.weight_kg || 0).toFixed(2)} kg × $${fmt(batch.kg_rate_usd)}/kg`}
                                </p>
                                {!item.user_id && (
                                  <p style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                                    No matched owner yet. Status can be updated, but no customer notification will be sent.
                                  </p>
                                )}
                              </div>

                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <span className={`badge badge-${item.payment_status}`} style={{ marginBottom: 4, display: 'block' }}>
                                  {item.payment_status}
                                </span>
                                <span className={`badge badge-${item.status}`} style={{ marginBottom: 6, display: 'block' }}>
                                  {formatStatus(item.status)}
                                </span>
                                <p style={{ fontSize: 11, color: '#6b7280' }}>USD ${fmt(item.final_fee_usd)}</p>
                                <p style={{ fontWeight: 800, fontSize: 14, color: '#1A6B8A' }}>GHS {fmt(item.final_fee)}</p>
                              </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr auto', gap: 8, marginTop: 10, alignItems: 'end' }}>
                              <div className="input-group" style={{ marginBottom: 0 }}>
                                <label>Shipment Status</label>
                                <select
                                  className="input-field"
                                  value={draft.status}
                                  onChange={(event) => updateItemDraft(item, { status: event.target.value })}
                                >
                                  {STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="input-group" style={{ marginBottom: 0 }}>
                                <label>Container Loaded</label>
                                <input
                                  className="input-field"
                                  type="date"
                                  value={draft.container_loaded_date}
                                  onChange={(event) => updateItemDraft(item, { container_loaded_date: event.target.value })}
                                />
                              </div>
                              <div className="input-group" style={{ marginBottom: 0 }}>
                                <label>ETA Ghana</label>
                                <input
                                  className="input-field"
                                  type="date"
                                  value={draft.eta_ghana}
                                  onChange={(event) => updateItemDraft(item, { eta_ghana: event.target.value })}
                                />
                              </div>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => saveSingleItem(batch.id, item)}
                                disabled={isSaving}
                              >
                                {isSaving ? <span className="spinner spinner-dark" /> : 'Save'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
